import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Globe, AlertTriangle, CalendarCheck, RefreshCw, Tags, Layers, Eye, Gavel, Archive, ShieldAlert, Tag, MessageSquare, CheckCircle2, ChevronRight, ArrowRightLeft, CalendarCheck2, XCircle, LogOut, WifiOff, History, Plug, Server, CalendarClock, Activity, TrendingDown, TrendingUp, CalendarX2, ShieldX, KeyRound, LayoutGrid, ListOrdered } from 'lucide-react';
import { useApi } from '../contexts/ApiContext';
import { Stats, Domain, ApiResponse } from '../types';
import { format, differenceInDays, formatDistanceToNowStrict } from 'date-fns';
import PageHeader from '../components/ui/PageHeader';
import EmptyState from '../components/ui/EmptyState';
import { Skeleton, SkeletonCard } from '../components/ui/Skeleton';
import { LineChart, SegmentBar, StackedBarChart } from '../components/charts';
import { LivenessDot } from '../components/LivenessBadge';
import AttentionTimeline from '../components/AttentionTimeline';
import AckForm, { type AckRef, type Acknowledged } from '../components/AckForm';

/** Every attention item carries the reference its Ack button sends. */
interface AttentionList<T> { total: number; items: (T & { ack?: AckRef })[] }
interface AttentionDomain { id: string; domain_name: string; expiry_date: string | null; liveness_status: string | null; domain_type: string | null; registrar_name: string | null }
interface Insights {
  expiring_by_month: { month: string; auto_renew: number; manual: number }[];
  liveness: { status: string; count: number }[];
  exposure_history: { at: string; domains_checked: number; domains_exposed: number; exposure_score: number }[] | null;
  attention: {
    expiring_no_auto_renew: AttentionList<AttentionDomain>;
    exposed_live: AttentionList<AttentionDomain> | null;
    renewals_unknown: AttentionList<{ id: string; domain_id: string; domain_name: string; updated_at: string; registrar_name?: string | null }> | null;
    ns_problems: AttentionList<{ id: string; domain_id: string; domain_name: string; status: string; message: string | null; created_at: string; registrar_name?: string | null }> | null;
    sync_failures: AttentionList<{ account_id: string; account: string; error_message: string | null; started_at: string }> | null;
    moved_away: AttentionList<{ id: string; domain_name: string; registrar_name: string | null; reason: string }> | null;
    services_failing?: AttentionList<{ service: string; last_error: string | null; error_kind: string | null; failing_since: string | null }> | null;
    transferred_out: AttentionList<{ id: string; domain_name: string; at: string; registrar_name: string | null; destination?: 'ours' | 'third_party' | 'unknown' | null; now_at?: string | null }>;
    renewals_failed: AttentionList<{ id: string; domain_id: string; domain_name: string; message: string | null; at: string; registrar_name?: string | null }> | null;
    went_offline: AttentionList<{ id: string; domain_name: string; domain_type: string | null; at: string; exposed: boolean; registrar_name?: string | null }>;
    renewal_missed?: AttentionList<{ id: string; domain_name: string; expiry_date: string; account: string }> | null;
    renewal_blocked?: AttentionList<{ account_id: string; account: string; due_30d: number; problem: string | null }> | null;
    accounts_without_api?: AttentionList<{ account_id: string; account: string; held_domains: number; missing: string[] }> | null;
    accounts_without_owner?: AttentionList<{ account_id: string; account: string; login: string; held_domains: number }> | null;
  };
  acknowledged?: Acknowledged[];
  history: { kind: 'renewal_succeeded' | 'renewal_failed' | 'ns_applied' | 'ns_failed' | 'transferred_out'; domain_id: string; domain_name: string; at: string; detail: string | null; actor: string | null }[];
}

const SERVICE_NAMES: Record<string, string> = { cove: 'Cove', onepassword: '1Password Connect', whoxy: 'Whoxy', slack: 'Slack' };

/** SQLite datetime('now') values are UTC without a zone marker. */
const parseTs = (v: string) => new Date(v.includes('T') ? v : `${v.replace(' ', 'T')}Z`);

const HISTORY_META: Record<Insights['history'][number]['kind'], { label: string; icon: React.ComponentType<{ className?: string }>; tone: string }> = {
  renewal_succeeded: { label: 'Renewed', icon: CalendarCheck2, tone: 'text-green-600' },
  renewal_failed: { label: 'Renewal failed', icon: XCircle, tone: 'text-red-600' },
  ns_applied: { label: 'Nameservers applied', icon: CheckCircle2, tone: 'text-green-600' },
  ns_failed: { label: 'Nameserver change failed', icon: XCircle, tone: 'text-red-600' },
  transferred_out: { label: 'Transferred out', icon: LogOut, tone: 'text-slate-500' },
};

const LIVENESS_SEGMENTS: { key: string; label: string; className: string }[] = [
  { key: 'live', label: 'Live', className: 'bg-green-500' },
  { key: 'redirect', label: 'Redirect', className: 'bg-blue-500' },
  { key: 'for_sale', label: 'For sale', className: 'bg-orange-500' },
  { key: 'parked', label: 'Parked', className: 'bg-amber-400' },
  { key: 'offline', label: 'Offline', className: 'bg-red-500' },
  { key: 'error', label: 'Check error', className: 'bg-slate-400' },
  { key: 'unchecked', label: 'Not checked', className: 'bg-slate-300' },
];

interface TagStat {
  tag_id: string;
  tag_name: string;
  color: string;
  count: number;
  expiring_no_renew_count: number;
}

export default function DashboardPage() {
  const { fetchApi, isAdmin } = useApi();
  const [stats, setStats] = useState<Stats | null>(null);
  const [expiringDomains, setExpiringDomains] = useState<Domain[]>([]);
  const [tagStats, setTagStats] = useState<TagStat[]>([]);
  const [exposureStats, setExposureStats] = useState<{ total_exposed: number; total_keywords: number } | null>(null);
  const [insights, setInsights] = useState<Insights | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadData() {
      try {
        const [statsRes, expiringRes, tagRes, exposureRes] = await Promise.all([
          fetchApi<ApiResponse<Stats>>('/reports/stats'),
          fetchApi<ApiResponse<Domain[]>>('/reports/expiring?days=30'),
          fetchApi<ApiResponse<TagStat[]>>('/reports/by-tag'),
          fetchApi<ApiResponse<{ total_exposed: number; total_keywords: number }>>('/whois/exposure/stats').catch(() => null),
        ]);

        if (statsRes.data) setStats(statsRes.data);
        if (expiringRes.data) setExpiringDomains(expiringRes.data);
        if (tagRes.data) setTagStats(tagRes.data);
        if (exposureRes?.data) setExposureStats(exposureRes.data);
      } catch (error) {
        console.error('Failed to load dashboard data:', error);
      } finally {
        setLoading(false);
      }
    }

    loadData();
  }, [fetchApi]);

  // Insights load on their own so a slow query never holds up the rest of the page.
  const [insightsState, setInsightsState] = useState<'loading' | 'ready' | 'error'>('loading');
  const [insightsVersion, setInsightsVersion] = useState(0); // bumped after an ack to refetch
  useEffect(() => {
    let cancelled = false;
    fetchApi<ApiResponse<Insights>>('/reports/insights')
      .then((res) => { if (!cancelled) { if (res.data) setInsights(res.data); setInsightsState('ready'); } })
      .catch(() => { if (!cancelled) setInsightsState('error'); });
    return () => { cancelled = true; };
  }, [fetchApi, insightsVersion]);

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-7 w-40" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {Array.from({ length: 4 }, (_, i) => <SkeletonCard key={i} lines={2} />)}
        </div>
        <SkeletonCard lines={6} />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <PageHeader title="Dashboard" description="Portfolio health at a glance." />
      </div>

      {/* What needs acting on comes first. */}
      {insights
        ? <AttentionPanel attention={insights.attention} history={insights.history} acknowledged={insights.acknowledged ?? []} onChanged={() => setInsightsVersion((v) => v + 1)} />
        : insightsState === 'loading' && <div className="mb-6"><SkeletonCard lines={5} /></div>}

      {/* Stats cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {/* Total Domains is a portfolio-wide figure — admins/super-admins only. */}
        {isAdmin && (
          <StatCard
            title="Total Domains"
            value={stats?.total_domains || 0}
            icon={Globe}
            color="brand"
            link="/domains"
          />
        )}
        <StatCard
          title="Active Domains"
          value={stats?.active_domains || 0}
          icon={Globe}
          color="emerald"
          link="/domains?status=active"
        />
        <StatCard
          title="Expiring Soon"
          value={stats?.expiring_30_days || 0}
          icon={AlertTriangle}
          color="amber"
          subtitle="Next 30 days"
          link="/domains?expiring_days=30"
        />
        <StatCard
          title="Auto-Renew Off"
          value={stats?.auto_renew_disabled || 0}
          icon={RefreshCw}
          color="rose"
          link="/domains?auto_renew=false"
        />
      </div>

      {insights ? <InsightsGrid insights={insights} /> : insightsState === 'loading' && (
        <div className="mb-8 grid grid-cols-1 gap-4 lg:grid-cols-3">
          <div className="lg:col-span-2"><SkeletonCard lines={6} /></div>
          <SkeletonCard lines={6} />
        </div>
      )}

      {/* Domain type cards — generated from live counts so any new
          classification (e.g. 'forum') surfaces here automatically. Known
          types get curated icon+color pairs; unknown types fall back to a
          stable hash-driven palette so they get a consistent appearance
          across reloads. */}
      {stats && Object.keys(stats.type_counts).length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {Object.entries(stats.type_counts).map(([type, count]) => {
            const { icon, color } = stylingForDomainType(type);
            // The Auction card also surfaces how many are ready for Auctopus.
            const subtitle = type.toLowerCase() === 'auction'
              ? `${(stats.auctopus_ready_count ?? 0).toLocaleString()} available to Auctopus`
              : undefined;
            return (
              <StatCard
                key={type}
                title={titleCase(type)}
                value={count}
                icon={icon}
                color={color}
                subtitle={subtitle}
                link={`/domains?domain_type=${encodeURIComponent(type)}`}
              />
            );
          })}
        </div>
      )}

      {/* Exposed domains warning */}
      {exposureStats && exposureStats.total_keywords > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard
            title="Exposed Domains"
            value={exposureStats.total_exposed}
            icon={ShieldAlert}
            color="rose"
            subtitle={`${exposureStats.total_keywords} keyword${exposureStats.total_keywords !== 1 ? 's' : ''} configured`}
            link="/whois"
          />
        </div>
      )}

      {/* Tags breakdown — admins/super-admins only */}
      {isAdmin && tagStats.length > 0 && (
        <div className="card mb-8">
          <div className="px-6 py-4 border-b border-slate-200">
            <h2 className="text-base font-semibold text-slate-900 flex items-center gap-2">
              <Tags className="h-4 w-4 text-slate-400" />
              Domains by Tag
            </h2>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {tagStats.map((tag) => (
                <Link
                  key={tag.tag_id}
                  to={`/domains?tag_id=${tag.tag_id}`}
                  className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 hover:border-slate-300 hover:shadow-card-hover transition-all duration-150"
                >
                  <div
                    className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                    style={{ backgroundColor: tag.color }}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-slate-900 truncate">{tag.tag_name}</div>
                    {tag.expiring_no_renew_count > 0 && (
                      <Link
                        to={`/domains?tag_id=${tag.tag_id}&expiring_days=30&auto_renew=false`}
                        className="text-xs text-red-600 hover:text-red-800 hover:underline"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {tag.expiring_no_renew_count} expiring, no auto-renew
                      </Link>
                    )}
                  </div>
                  <span className="text-lg font-bold text-slate-600">{tag.count}</span>
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Expiring domains */}
      <div className="card">
        <div className="px-6 py-4 border-b border-slate-200">
          <h2 className="text-base font-semibold text-slate-900">
            Domains Expiring Soon
          </h2>
          <p className="text-sm text-slate-500 mt-0.5">
            Domains expiring in the next 30 days
          </p>
        </div>

        {expiringDomains.length === 0 ? (
          <EmptyState compact icon={CalendarCheck} title="No domains expiring in the next 30 days" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr>
                  <th className="table-header">Domain</th>
                  <th className="table-header">Registrar</th>
                  <th className="table-header">Expiry Date</th>
                  <th className="table-header">Days Left</th>
                  <th className="table-header">Auto-Renew</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {expiringDomains.slice(0, 10).map((domain, idx) => {
                  const daysLeft = domain.expiry_date
                    ? differenceInDays(new Date(domain.expiry_date), new Date())
                    : null;

                  return (
                    <tr key={domain.id} className={`table-row ${idx % 2 === 1 ? 'table-row-alt' : ''}`}>
                      <td className="table-cell">
                        <Link
                          to={`/domains/${domain.id}`}
                          className="text-brand-600 hover:text-brand-800 font-medium"
                        >
                          {domain.domain_name}
                        </Link>
                      </td>
                      <td className="table-cell text-slate-500">
                        {domain.registrar_name || domain.registrar_id}
                      </td>
                      <td className="table-cell">
                        {domain.expiry_date
                          ? format(new Date(domain.expiry_date), 'MMM d, yyyy')
                          : '-'}
                      </td>
                      <td className="table-cell">
                        {daysLeft !== null && (
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                              daysLeft <= 7
                                ? 'bg-red-100 text-red-700'
                                : daysLeft <= 14
                                ? 'bg-amber-100 text-amber-700'
                                : 'bg-slate-100 text-slate-600'
                            }`}
                          >
                            {daysLeft} days
                          </span>
                        )}
                      </td>
                      <td className="table-cell">
                        {domain.auto_renew === null ? (
                          <span className="text-slate-400 font-medium">-</span>
                        ) : domain.auto_renew ? (
                          <span className="text-emerald-600 font-medium">Yes</span>
                        ) : (
                          <span className="text-red-600 font-medium">No</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {expiringDomains.length > 10 && (
          <div className="px-6 py-4 border-t border-slate-200 bg-slate-50">
            <Link
              to="/domains?expiring_days=30"
              className="text-brand-600 hover:text-brand-800 text-sm font-medium"
            >
              View all {expiringDomains.length} expiring domains
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({
  title,
  value,
  icon: Icon,
  color,
  subtitle,
  link,
}: {
  title: string;
  value: number;
  icon: React.ComponentType<{ className?: string }>;
  color: 'brand' | 'emerald' | 'amber' | 'rose' | 'slate';
  subtitle?: string;
  link?: string;
}) {
  const colorClasses = {
    brand: 'bg-brand-100 text-brand-600',
    emerald: 'bg-emerald-100 text-emerald-600',
    amber: 'bg-amber-100 text-amber-600',
    rose: 'bg-rose-100 text-rose-600',
    slate: 'bg-slate-100 text-slate-600',
  };

  const content = (
    <div className="flex items-center gap-4">
      <div className={`p-2.5 rounded-lg ${colorClasses[color]}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div>
        <p className="text-sm text-slate-500">{title}</p>
        <p className="text-2xl font-bold text-slate-900">{value}</p>
        {subtitle && <p className="text-xs text-slate-400">{subtitle}</p>}
      </div>
    </div>
  );

  if (link) {
    return (
      <Link to={link} className="card p-5 hover:shadow-card-hover hover:border-slate-300 transition-all duration-150 cursor-pointer">
        {content}
      </Link>
    );
  }

  return <div className="card p-5">{content}</div>;
}

// Curated icon+color for known domain_type values; unknown types pick a
// color from the palette via a stable hash so the same type always renders
// the same way across reloads.
type StatColor = 'brand' | 'emerald' | 'amber' | 'rose' | 'slate';
const KNOWN_DOMAIN_TYPE_STYLE: Record<string, { icon: React.ComponentType<{ className?: string }>; color: StatColor }> = {
  'o&o': { icon: Layers, color: 'brand' },
  'cloaking': { icon: Eye, color: 'amber' },
  'auction': { icon: Gavel, color: 'emerald' },
  'uncategorized': { icon: Archive, color: 'slate' },
  'forum': { icon: MessageSquare, color: 'rose' },
};
const FALLBACK_PALETTE: StatColor[] = ['brand', 'emerald', 'amber', 'rose', 'slate'];

function stylingForDomainType(type: string): { icon: React.ComponentType<{ className?: string }>; color: StatColor } {
  const known = KNOWN_DOMAIN_TYPE_STYLE[type.toLowerCase()];
  if (known) return known;
  // Stable hash → palette index so 'forum' (or whatever) always lands on
  // the same color and icon between reloads.
  let hash = 0;
  for (let i = 0; i < type.length; i++) hash = (hash * 31 + type.charCodeAt(i)) | 0;
  return { icon: Tag, color: FALLBACK_PALETTE[Math.abs(hash) % FALLBACK_PALETTE.length] };
}

function titleCase(value: string): string {
  // 'o&o' stays as-is; multi-word values get title-cased per word.
  if (value === value.toUpperCase() || value.includes('&')) return value;
  return value.replace(/\b\w/g, c => c.toUpperCase());
}

function monthLabel(m: string, withYear = false): string {
  const [y, mo] = m.split('-').map(Number);
  const d = new Date(Date.UTC(y, mo - 1, 1));
  return d.toLocaleString(undefined, { month: 'short', timeZone: 'UTC' }) + (withYear ? ` '${String(y).slice(2)}` : '');
}

function InsightsGrid({ insights }: { insights: Insights }) {
  const navigate = useNavigate();
  // Next 12 months, including months with nothing expiring.
  const now = new Date();
  const months = Array.from({ length: 12 }, (_, i) => {
    const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + i, 1));
    return d.toISOString().slice(0, 7);
  });
  const byMonth = new Map(insights.expiring_by_month.map((r) => [r.month, r]));
  const monthData = months.map((m) => ({ label: m, values: { auto_renew: byMonth.get(m)?.auto_renew ?? 0, manual: byMonth.get(m)?.manual ?? 0 } }));
  const yearTotal = monthData.reduce((s, d) => s + d.values.auto_renew + d.values.manual, 0);
  const manualTotal = monthData.reduce((s, d) => s + d.values.manual, 0);

  const live = new Map(insights.liveness.map((r) => [r.status, r.count]));
  const segments = LIVENESS_SEGMENTS.map((s) => ({
    ...s,
    value: live.get(s.key) ?? 0,
    to: `/domains?liveness_status=${s.key === 'unchecked' ? '__none__' : s.key}`,
  })).filter((s) => s.value > 0);

  const hist = insights.exposure_history;
  const lastTwo = hist && hist.length >= 2 ? [hist[hist.length - 2], hist[hist.length - 1]] : null;
  const delta = lastTwo ? lastTwo[1].domains_exposed - lastTwo[0].domains_exposed : 0;

  return (
    <div className="mb-8 grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="card p-5 lg:col-span-2">
          <div className="mb-4 flex flex-wrap items-baseline justify-between gap-2">
            <div>
              <h2 className="text-base font-semibold text-slate-900">Renewals ahead</h2>
              <p className="text-sm text-slate-500">Active domains expiring per month, next 12 months</p>
            </div>
            <div className="text-right text-sm">
              <span className="font-semibold text-slate-900 tabular-nums">{yearTotal.toLocaleString()}</span>
              <span className="text-slate-500"> expiring · </span>
              <Link to="/domains?auto_renew=false" className="font-medium text-amber-600 hover:underline tabular-nums">{manualTotal.toLocaleString()} without auto-renew</Link>
            </div>
          </div>
          <StackedBarChart
            data={monthData}
            series={[
              { key: 'auto_renew', label: 'Auto-renew on', className: 'bg-brand-500' },
              { key: 'manual', label: 'Auto-renew off / unknown', className: 'bg-amber-400' },
            ]}
            formatLabel={(m) => monthLabel(m, m.endsWith('-01') || m === months[0])}
            // Drill down: that month's active domains with auto-renew on, or off/unknown (amber).
            onSegmentClick={(m, key) => {
              const [y, mo] = m.split('-').map(Number);
              const next = new Date(Date.UTC(y, mo, 1)).toISOString().slice(0, 10);
              const today = new Date().toISOString().slice(0, 10);
              const from = m === months[0] && `${m}-01` < today ? today : `${m}-01`;
              navigate(`/domains?status=active&expires_from=${from}&expires_to=${next}&auto_renew=${key === 'auto_renew' ? 'true' : 'not_on'}&sort_by=expiry_date&sort_order=asc`);
            }}
          />
        </div>

          <div className="card p-5">
            <h2 className="flex items-center gap-2 text-base font-semibold text-slate-900"><Activity className="h-4 w-4 text-slate-400" />Site status</h2>
            <p className="mb-4 text-sm text-slate-500">Latest liveness check, active domains</p>
            <SegmentBar segments={segments} />
          </div>

          {hist ? (
            <div className="card p-5 lg:col-span-3">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <h2 className="flex items-center gap-2 text-base font-semibold text-slate-900"><ShieldAlert className="h-4 w-4 text-slate-400" />WHOIS exposure</h2>
                  <p className="mb-4 text-sm text-slate-500">Exposed domains per footprint audit</p>
                </div>
                {hist.length > 0 && (
                  <div className="text-right">
                    <div className="text-2xl font-semibold tabular-nums text-slate-900">{hist[hist.length - 1].domains_exposed}</div>
                    {lastTwo && delta !== 0 && (
                      <div className={`inline-flex items-center gap-0.5 text-xs font-medium ${delta > 0 ? 'text-red-600' : 'text-green-600'}`}>
                        {delta > 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                        {delta > 0 ? '+' : ''}{delta} since last audit
                      </div>
                    )}
                  </div>
                )}
              </div>
              <LineChart
                points={hist.map((h) => ({ x: h.at, y: h.domains_exposed }))}
                formatX={(x) => format(new Date(x), 'MMM d')}
                label="exposed"
              />
              <Link to="/footprint-audit" className="mt-3 inline-flex items-center text-xs font-medium text-brand-600 hover:underline">Open Footprint Audit <ChevronRight className="h-3 w-3" /></Link>
            </div>
          ) : (
            <div className="card p-5 lg:col-span-3">
              <h2 className="flex items-center gap-2 text-base font-semibold text-slate-900"><CalendarClock className="h-4 w-4 text-slate-400" />Expiring soon</h2>
              <p className="mb-4 text-sm text-slate-500">Next 30 days</p>
              <div className="text-3xl font-semibold tabular-nums text-slate-900">{monthData.slice(0, 1).reduce((s, d) => s + d.values.auto_renew + d.values.manual, 0)}</div>
              <Link to="/domains?expiring_days=30" className="mt-3 inline-flex items-center text-xs font-medium text-brand-600 hover:underline">View domains <ChevronRight className="h-3 w-3" /></Link>
            </div>
          )}
    </div>
  );
}

interface AttentionRow { key: string; to: string; label: string; sub?: string; right?: string; dot?: string | null; tone?: 'bad'; ack?: AckRef }
interface AttentionSection { key: string; urgent: boolean; title: string; hint: string; icon: React.ComponentType<{ className?: string }>; total: number; viewAll?: string; rows: AttentionRow[] }

function AttentionSectionCard({ sec, acked, onChanged }: { sec: AttentionSection; acked: Acknowledged[]; onChanged: () => void }) {
  const { fetchApi } = useApi();
  const [acking, setAcking] = useState<string | null>(null);
  const undo = async (id: string) => { await fetchApi(`/reports/attention/acks/${encodeURIComponent(id)}`, { method: 'DELETE' }).catch(() => undefined); onChanged(); };
  return (
    <div className={`bg-surface px-5 py-4 ${sec.urgent ? 'border-l-2 border-l-red-500' : ''}`}>
      <div className="flex items-start gap-2.5">
        <span className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md ${sec.urgent ? 'bg-red-50 text-red-600' : 'bg-amber-50 text-amber-600'}`}><sec.icon className="h-4 w-4" /></span>
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline justify-between gap-2">
            <h3 className="text-sm font-semibold text-slate-900">{sec.title}</h3>
            <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-semibold tabular-nums ${sec.urgent ? 'bg-red-50 text-red-700' : 'bg-amber-50 text-amber-700'}`}>{sec.total}</span>
          </div>
          <p className="mt-0.5 text-xs leading-snug text-slate-500">{sec.hint}</p>
        </div>
      </div>
      <ul className="mt-3 divide-y divide-slate-100">
        {sec.rows.slice(0, 5).map((r) => (
          <li key={r.key} className="py-1.5">
            <div className="flex items-start gap-2">
              <Link to={r.to} className="group min-w-0 flex-1">
                <span className="flex items-center gap-1.5">
                  {r.dot !== undefined && <LivenessDot status={r.dot} />}
                  <span className="truncate text-sm font-medium text-brand-600 group-hover:underline">{r.label}</span>
                </span>
                {r.sub && <span className={`block truncate text-xs ${r.tone === 'bad' ? 'text-red-600' : 'text-slate-500'}`} title={r.sub}>{r.sub}</span>}
              </Link>
              <div className="flex shrink-0 items-center gap-2 pt-0.5">
                {r.right && <span className="text-xs tabular-nums text-slate-500">{r.right}</span>}
                {r.ack && acking !== r.key && (
                  <button type="button" onClick={() => setAcking(r.key)} title="Acknowledge with a comment"
                    className="rounded border border-slate-200 px-1.5 py-0.5 text-[11px] font-medium text-slate-500 hover:border-slate-300 hover:text-slate-800">Ack</button>
                )}
              </div>
            </div>
            {r.ack && acking === r.key && <AckForm ack={r.ack} onCancel={() => setAcking(null)} onDone={() => { setAcking(null); onChanged(); }} />}
          </li>
        ))}
      </ul>
      {sec.viewAll && sec.total > 5 && (
        <Link to={sec.viewAll} className="mt-1 inline-flex items-center text-xs font-medium text-brand-600 hover:underline">
          View all {sec.total} <ChevronRight className="h-3 w-3" />
        </Link>
      )}
      {acked.length > 0 && (
        <details className="mt-2 border-t border-slate-100 pt-2">
          <summary className="flex cursor-pointer list-none items-center gap-1 text-xs text-slate-500 hover:text-slate-700">
            <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />{acked.length} acknowledged
          </summary>
          <ul className="mt-1.5 space-y-1.5">
            {acked.map((k) => (
              <li key={k.id} className="text-xs">
                <div className="flex items-baseline justify-between gap-2">
                  <span className="truncate font-medium text-slate-700">{k.label}</span>
                  <button type="button" onClick={() => undo(k.id)} className="shrink-0 text-[11px] text-brand-600 hover:underline">Undo</button>
                </div>
                <p className="text-slate-600">“{k.comment}”</p>
                <p className="text-slate-400">{k.by} · {formatDistanceToNowStrict(parseTs(k.at), { addSuffix: true })}</p>
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

function AttentionPanel({ attention, history, acknowledged, onChanged }: { attention: Insights['attention']; history: Insights['history']; acknowledged: Acknowledged[]; onChanged: () => void }) {
  // "By type" cards or the timeline; remembered per browser.
  const [view, setView] = useState<'type' | 'timeline'>(() => {
    try { return localStorage.getItem('attentionView') === 'timeline' ? 'timeline' : 'type'; } catch { return 'type'; }
  });
  const chooseView = (v: 'type' | 'timeline') => {
    setView(v);
    try { localStorage.setItem('attentionView', v); } catch { /* per-viewer convenience only */ }
  };

  const a = attention;
  const day = (d: string | null | undefined) => (d ? format(parseTs(d), 'd MMM') : undefined);
  const inDays = (d: string | null) => { if (!d) return undefined; const n = differenceInDays(new Date(d), new Date()); return n <= 0 ? 'today' : `in ${n}d`; };
  const ago = (d: string | null | undefined) => (d ? formatDistanceToNowStrict(parseTs(d), { addSuffix: true }) : undefined);
  const clean = (s: string | null | undefined, n = 90) => (s ? s.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim().slice(0, n) : undefined);

  // Ordered by urgency. "Act now" items cost money or domains if left; "Review" can wait a day.
  const sections: AttentionSection[] = [];
  const add = (sec: AttentionSection | false | 0 | undefined | null) => { if (sec) sections.push(sec); };
  add(!!a.renewal_missed?.total && {
    key: 'renewal_missed', urgent: true, title: 'Auto-renewals that didn\'t go through', icon: CalendarX2,
    hint: 'Auto-renew was on but the registrar didn\'t renew — fix the payment method and renew before the grace period ends.',
    total: a.renewal_missed.total, viewAll: '/domains?renewal_missed=true',
    rows: a.renewal_missed.items.map((d) => ({ key: d.id, to: `/domains/${d.id}`, label: d.domain_name, sub: d.account, right: `expired ${day(d.expiry_date)}`, ack: d.ack })),
  });
  add(!!a.renewal_blocked?.total && {
    key: 'renewal_blocked', urgent: true, title: 'Accounts that can\'t renew', icon: ShieldX,
    hint: 'Domains are coming due on accounts that can\'t renew them as things stand.',
    total: a.renewal_blocked.total, viewAll: '/renewal-readiness',
    rows: a.renewal_blocked.items.map((x) => ({ key: x.account_id, to: '/renewal-readiness', label: x.account, sub: x.problem ?? undefined, right: x.due_30d ? `${x.due_30d} due` : undefined, ack: x.ack })),
  });
  add(!!a.renewals_failed?.total && {
    key: 'renewals_failed', urgent: true, title: 'Renewals failed (last 30 days)', icon: XCircle,
    hint: 'The registrar refused a renewal sent from DomainHQ — usually payment. Nothing was charged.',
    total: a.renewals_failed.total,
    rows: a.renewals_failed.items.map((r) => ({ key: r.id, to: `/domains/${r.domain_id}`, label: r.domain_name, sub: [r.registrar_name, clean(r.message)].filter(Boolean).join(' · ') || undefined, right: ago(r.at), ack: r.ack })),
  });
  add(!!a.renewals_unknown?.total && {
    key: 'renewals_unknown', urgent: true, title: 'Renewals with unknown outcome', icon: AlertTriangle,
    hint: 'Check at the registrar before retrying — it may already have charged.',
    total: a.renewals_unknown.total,
    rows: a.renewals_unknown.items.map((r) => ({ key: r.id, to: `/domains/${r.domain_id}`, label: r.domain_name, sub: r.registrar_name ?? undefined, right: ago(r.updated_at), ack: r.ack })),
  });
  add(!!a.services_failing?.total && {
    key: 'services_failing', urgent: true, title: 'External services failing', icon: Plug,
    hint: 'DomainHQ can\'t reach a service it relies on.',
    total: a.services_failing.total, viewAll: '/integrations',
    rows: a.services_failing.items.map((x) => ({ key: x.service, to: '/integrations', label: SERVICE_NAMES[x.service] ?? x.service, sub: clean(x.last_error), right: ago(x.failing_since), ack: x.ack })),
  });
  add(!!a.sync_failures?.total && {
    key: 'sync_failures', urgent: true, title: 'Registrar syncs failing', icon: Server,
    hint: 'These accounts aren\'t syncing, so their expiry and status data is going stale.',
    total: a.sync_failures.total, viewAll: '/registrars',
    rows: a.sync_failures.items.map((f) => ({ key: f.account_id, to: '/registrars', label: f.account, sub: clean(f.error_message), right: ago(f.started_at), ack: f.ack })),
  });
  add(!!a.transferred_out.total && {
    key: 'transferred_out', urgent: a.transferred_out.items.some((d) => d.destination === 'third_party'), title: 'Transferred out (last 30 days)', icon: LogOut,
    hint: 'Left their registrar account — check where they went (third party = not one of ours).',
    total: a.transferred_out.total, viewAll: '/domains?status=transferred_out',
    rows: a.transferred_out.items.map((d) => ({ key: d.id, to: `/domains/${d.id}`, label: d.domain_name,
      sub: [d.registrar_name ? `from ${d.registrar_name}` : null, d.destination === 'third_party' ? `→ third party${d.now_at ? ` (${d.now_at})` : ''}` : d.destination === 'ours' ? `→ ${d.now_at ?? 'ours'} (ours)` : null].filter(Boolean).join(' ') || undefined,
      right: ago(d.at), tone: d.destination === 'third_party' ? 'bad' as const : undefined, ack: d.ack })),
  });
  add(!!a.exposed_live?.total && {
    key: 'exposed_live', urgent: true, title: 'Exposed in WHOIS and live', icon: ShieldAlert,
    hint: 'Live sites whose WHOIS record gives away who owns them.',
    total: a.exposed_live.total, viewAll: '/domains?exposed=true&liveness_status=live',
    rows: a.exposed_live.items.map((d) => ({ key: d.id, to: `/domains/${d.id}`, label: d.domain_name, sub: [d.registrar_name, d.domain_type].filter(Boolean).join(' · ') || undefined, dot: d.liveness_status, ack: d.ack })),
  });
  add(!!a.expiring_no_auto_renew.total && {
    key: 'expiring_no_auto_renew', urgent: false, title: 'Expiring in 30 days, auto-renew off', icon: CalendarClock,
    hint: 'Renew or turn auto-renew on — or acknowledge the ones meant to lapse.',
    total: a.expiring_no_auto_renew.total, viewAll: '/domains?expiring_days=30&auto_renew=false',
    rows: a.expiring_no_auto_renew.items.map((d) => ({ key: d.id, to: `/domains/${d.id}`, label: d.domain_name, sub: d.registrar_name ?? undefined, right: inDays(d.expiry_date), ack: d.ack })),
  });
  add(!!a.ns_problems?.total && {
    key: 'ns_problems', urgent: false, title: 'Nameserver changes failed or stalled', icon: Globe,
    hint: 'The registrar hasn\'t applied a nameserver change.',
    total: a.ns_problems.total,
    rows: a.ns_problems.items.map((n) => ({ key: n.id, to: `/domains/${n.domain_id}`, label: n.domain_name, sub: [n.registrar_name, n.status].filter(Boolean).join(' · ') || undefined, right: ago(n.created_at), ack: n.ack })),
  });
  add(!!a.went_offline.total && {
    key: 'went_offline', urgent: false, title: 'Went offline (last 7 days)', icon: WifiOff,
    hint: 'Was live at the previous check.',
    total: a.went_offline.total, viewAll: '/domains?liveness_status=offline',
    rows: a.went_offline.items.map((d) => ({ key: d.id, to: `/domains/${d.id}`, label: d.domain_name, sub: [d.registrar_name, d.exposed ? 'WHOIS-exposed' : null, d.domain_type].filter(Boolean).join(' · ') || undefined, right: ago(d.at), dot: 'offline', ack: d.ack })),
  });
  add(!!a.accounts_without_api?.total && {
    key: 'accounts_without_api', urgent: false, title: 'Accounts that can\'t sync', icon: KeyRound,
    hint: 'Add the API credentials to the account\'s 1Password item — picked up before the next sync.',
    total: a.accounts_without_api.total, viewAll: '/renewal-readiness',
    rows: a.accounts_without_api.items.map((x) => ({ key: x.account_id, to: '/renewal-readiness', label: x.account, sub: `add ${x.missing.join(', ')}`, right: x.held_domains ? `${x.held_domains} domains` : undefined, ack: x.ack })),
  });
  add(!!a.accounts_without_owner?.total && {
    key: 'accounts_without_owner', urgent: false, title: 'Accounts without an owner', icon: Tags,
    hint: 'Set the owner entity and purpose so the account gets its standard name.',
    total: a.accounts_without_owner.total, viewAll: '/account-names',
    rows: a.accounts_without_owner.items.map((x) => ({ key: x.account_id, to: '/account-names', label: x.account, right: x.held_domains ? `${x.held_domains} domains` : undefined, ack: x.ack })),
  });
  add(!!a.moved_away?.total && {
    key: 'moved_away', urgent: false, title: 'Probably moved to another registrar', icon: ArrowRightLeft,
    hint: 'WHOIS shows a different registrar than the account that lists it.',
    total: a.moved_away.total, viewAll: '/domains?moved_away=true',
    rows: a.moved_away.items.map((d) => ({ key: d.id, to: `/domains/${d.id}`, label: d.domain_name, sub: [d.registrar_name, `WHOIS: ${d.reason.replace(/^WHOIS shows /, '').split(' (')[0]}`].filter(Boolean).join(' · '), ack: d.ack })),
  });
  // Urgent first, keeping the order above within each band.
  const actNow = sections.filter((x) => x.urgent);
  const review = sections.filter((x) => !x.urgent);
  const ackedBy = new Map<string, Acknowledged[]>();
  for (const k of acknowledged) ackedBy.set(k.kind, [...(ackedBy.get(k.kind) ?? []), k]);

  return (
    <div className="card mb-6 overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 px-5 py-4">
        <div className="flex items-center gap-2">
          <h2 className="text-base font-semibold text-slate-900">Needs attention</h2>
          {sections.length > 0 && (
            <span className="rounded-full bg-red-50 px-2 py-0.5 text-xs font-semibold text-red-700 tabular-nums">
              {sections.reduce((n, x) => n + x.total, 0)}
            </span>
          )}
          {acknowledged.length > 0 && <span className="text-xs text-slate-400">· {acknowledged.length} acknowledged</span>}
        </div>
        <div className="inline-flex rounded-md border border-slate-200 p-0.5 text-xs" role="group" aria-label="Needs attention view">
          {([['type', 'By type', LayoutGrid], ['timeline', 'Timeline', ListOrdered]] as const).map(([v, label, Icon]) => (
            <button key={v} type="button" onClick={() => chooseView(v)} aria-pressed={view === v}
              className={`inline-flex items-center gap-1 rounded px-2 py-1 font-medium ${view === v ? 'bg-slate-100 text-slate-900' : 'text-slate-500 hover:text-slate-700'}`}>
              <Icon className="h-3.5 w-3.5" />{label}
            </button>
          ))}
        </div>
      </div>
      {view === 'timeline' ? <AttentionTimeline onChanged={onChanged} /> : (
      <div className={`grid grid-cols-1 ${history.length > 0 ? 'xl:grid-cols-[minmax(0,1fr)_22rem]' : ''}`}>
      {sections.length === 0 ? (
        <EmptyState compact icon={CheckCircle2} title="All clear" description={acknowledged.length ? `Nothing open — ${acknowledged.length} item${acknowledged.length === 1 ? '' : 's'} acknowledged.` : 'Nothing expiring without auto-renew, no exposed live sites, and no failed syncs or changes.'} />
      ) : (
        <div>
          {[{ label: 'Act now', tone: 'text-red-700', list: actNow }, { label: 'Review', tone: 'text-amber-700', list: review }].filter((b) => b.list.length).map((band) => (
            <div key={band.label}>
              <div className={`border-b border-slate-100 bg-slate-50/60 px-5 py-1.5 text-[11px] font-semibold uppercase tracking-wider ${band.tone}`}>{band.label}</div>
              {/* Sections flow into 1 / 2 / 3 columns with the screen width. */}
              <div className="grid grid-cols-1 gap-px bg-slate-100 md:grid-cols-2 2xl:grid-cols-3">
                {band.list.map((sec) => <AttentionSectionCard key={sec.key} sec={sec} acked={ackedBy.get(sec.key) ?? []} onChanged={onChanged} />)}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Resolved and notable events, so cleared items don't just vanish. */}
      {history.length > 0 && (
        <div className="border-t border-slate-200 px-5 py-3.5 xl:border-l xl:border-t-0">
          <h3 className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-slate-500">
            <History className="h-3.5 w-3.5" /> Last 7 days
          </h3>
          <ul className="space-y-1">
            {history.map((h, i) => {
              const meta = HISTORY_META[h.kind];
              return (
                <li key={`${h.kind}-${h.domain_id}-${i}`}>
                  {/* Two lines so a long actor (API key names) never pushes the event off-screen. */}
                  <Link to={`/domains/${h.domain_id}`} className="-mx-1.5 flex items-start gap-2 rounded px-1.5 py-1 text-sm hover:bg-slate-50"
                    title={[`${meta.label} · ${h.domain_name}`, h.detail, h.actor].filter(Boolean).join('\n')}>
                    <meta.icon className={`mt-0.5 h-3.5 w-3.5 shrink-0 ${meta.tone}`} />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-slate-700"><span className="text-slate-500">{meta.label}</span> · {h.domain_name}</span>
                      <span className="block truncate text-xs text-slate-400">
                        {h.actor && <>{h.actor} · </>}
                        {formatDistanceToNowStrict(parseTs(h.at), { addSuffix: true })}
                      </span>
                    </span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>
      )}
      </div>
      )}
    </div>
  );
}
